import { useEffect, useMemo, useState } from "react";
import { Button, Field, Spinner } from "@fluentui/react-components";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { showToast } from "#root/components/GlobalToaster";
import { useCurrentTenant } from "#root/queries/local";
import { useTenantById } from "#root/queries/tenants";
import {
    ApiActiveSettings,
    TENANT_API_SETTING_NAMES,
    useTenantApiActiveSettings,
    useUpdateTenantSettings
} from "#root/queries/tenantSettings";
import {
    API_SETTING_LABELS,
    ApiSettingKey
} from "#pages/Home/OrganizationForm";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import "./styles.scss";

const ManagerSettings: React.FC = () => {
    const navigate = useNavigate();
    const { data: currentTenantId } = useCurrentTenant();
    const { data: tenantResponse, isLoading: isTenantLoading } = useTenantById(
        { id: currentTenantId || "" },
        { refetchOnMount: 'always', refetchOnWindowFocus: true, staleTime: 0 }
    );
    const {
        data: activeApiSettings,
        isLoading: isActiveLoading,
        refetch: refetchActive
    } = useTenantApiActiveSettings(
        currentTenantId ?? undefined,
        { refetchOnMount: 'always', refetchOnWindowFocus: true, staleTime: 0 }
    );

    const updateTenantSettings = useUpdateTenantSettings();
    const [localActive, setLocalActive] = useState<ApiActiveSettings | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const tenant = tenantResponse?.item;

    const allowedApis = useMemo<ApiSettingKey[]>(() => {
        if (!tenant) return [];
        const map: Record<ApiSettingKey, boolean | undefined> = {
            metroPcrVictims: tenant.metroPcrVictims,
            metroPcrContacts: tenant.metroPcrContacts,
            webEocVictims: tenant.webEocVictims
        };
        return (Object.keys(map) as ApiSettingKey[]).filter(k => map[k]);
    }, [tenant]);

    useEffect(() => {
        if (activeApiSettings) {
            setLocalActive(activeApiSettings);
        }
    }, [activeApiSettings]);

    const handleToggle = (key: ApiSettingKey, checked: boolean) => {
        setLocalActive(prev => prev ? { ...prev, [key]: checked } : prev);
    };

    const handleCancel = () => {
        setLocalActive(prev => {
            if (!prev) return prev;
            const cleared = { ...prev };
            allowedApis.forEach(key => { cleared[key] = false; });
            return cleared;
        });
        navigate(ROUTES.ROOT);
    };

    const handleSave = async () => {
        if (!currentTenantId || !localActive) return;
        setIsSaving(true);
        try {
            const settings = allowedApis.map(key => ({
                name: TENANT_API_SETTING_NAMES[key],
                value: String(!!localActive[key])
            }));
            await updateTenantSettings.mutateAsync({
                tenantId: currentTenantId,
                settings
            });
            showToast("Manager settings updated successfully", "success");
            refetchActive();
        } catch (error) {
            if (error instanceof AxiosError) {
                const errorMessage = error.response?.data?.Message || "Failed to update manager settings";
                showToast(errorMessage, "error");
            } else {
                showToast("Failed to update manager settings", "error");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isLoading = isTenantLoading || isActiveLoading || !localActive;

    return (
        <div className="manager-settings">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>Manager Settings <div className="divider" /></h2>
                </div>
            </div>

            <div className="manager-settings__content">
                {isLoading && <Spinner size="large" />}
                {!isLoading && (
                    <>
                        <div className="section-title">API Settings</div>
                        {allowedApis.length === 0 && (
                            <div className="section-subtitle">No APIs enabled for this organization.</div>
                        )}
                        <Field>
                            <div className="api-settings">
                                {allowedApis.map(key => (
                                    <Button
                                        key={key}
                                        type="button"
                                        appearance={localActive![key] ? "primary" : "secondary"}
                                        onClick={() => handleToggle(key, !localActive![key])}
                                    >
                                        {API_SETTING_LABELS[key]}
                                    </Button>
                                ))}
                            </div>
                        </Field>

                        {allowedApis.length > 0 && (
                            <div className="manager-settings__actions">
                                <Button
                                    appearance="primary"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? "Saving..." : "Save"}
                                </Button>
                                <Button
                                    appearance="secondary"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ManagerSettings;
